import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart } from '../../components/primitives/PGChart';
import { getBranch } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const { FiPackage, FiAlertCircle, FiRepeat, FiTruck, FiMinusCircle } = FiIcons;

const STOCK_ROWS = [
  { sku: 'WS-TOY-HLX-F', desc: 'Toyota Hilux front', onHand: 6, reorder: 4 },
  { sku: 'WS-FRD-RGR-F', desc: 'Ford Ranger front', onHand: 2, reorder: 4 },
  { sku: 'WS-VW-POLO-F', desc: 'VW Polo front', onHand: 8, reorder: 5 },
  { sku: 'WS-BMW-3S-F', desc: 'BMW 3-Series front', onHand: 3, reorder: 3 },
  { sku: 'WS-MZD-BT50-F', desc: 'Mazda BT-50 front', onHand: 5, reorder: 4 },
  { sku: 'WS-NIS-NP200-R', desc: 'Nissan NP200 rear', onHand: 7, reorder: 4 },
  { sku: 'WS-HYU-I20-F', desc: 'Hyundai i20 front', onHand: 4, reorder: 4 },
  { sku: 'WS-KIA-PIC-F', desc: 'Kia Picanto front', onHand: 6, reorder: 3 },
  { sku: 'ADH-URETH-310', desc: 'Urethane adhesive', onHand: 22, reorder: 12 },
  { sku: 'PRMR-GL-250', desc: 'Glass primer 250ml', onHand: 9, reorder: 8 },
];

const OPEN_POS = [
  { po: 'PO-48812', supplier: 'PGSA Dist', lines: 6, value: 'R 82 400', eta: '2d', tone: 'ok' },
  { po: 'PO-48819', supplier: 'AIS Glass', lines: 3, value: 'R 38 900', eta: '4d', tone: 'ok' },
  { po: 'PO-48821', supplier: 'Shatterprufe', lines: 12, value: 'R 146 200', eta: '1d', tone: 'warn' },
  { po: 'PO-48825', supplier: 'XYG SA', lines: 2, value: 'R 22 100', eta: '7d', tone: 'ok' },
];

function consumptionSeries(seed) {
  const out = [];
  let v = 28;
  for (let i = 0; i < 7; i++) {
    v = Math.max(12, Math.round(v + Math.sin(i * 1.1 + seed) * 8));
    out.push(v);
  }
  return out;
}

const statusOf = (onHand, reorder) => {
  if (onHand === 0) return { tone: 'alert', label: 'STOCKOUT' };
  if (onHand <= reorder) return { tone: 'warn', label: 'REORDER' };
  return { tone: 'ok', label: 'OK' };
};

export default function SupplyBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);

  const consumption = useMemo(() => consumptionSeries((branch?.id ?? '').length), [branch]);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">Supply / Branch drilldown</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-3xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{branch.town} &middot; {branch.country}</span>
          </div>
        </div>
        <StatusPill tone={branch.supply.stockAvailability >= 95 ? 'ok' : 'warn'}>Peer: {branch.peerGroup}</StatusPill>
      </div>

      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="AVAILABILITY" value={branch.supply.stockAvailability} unit="%" icon={FiPackage} accent={PG.emerald} target=">95%" />
        <KPICard title="STOCKOUTS" value={branch.supply.stockoutEvents} icon={FiAlertCircle} accent={branch.supply.stockoutEvents > 2 ? PG.red : PG.amber} />
        <KPICard title="TURN DAYS" value={branch.supply.stockTurnDays} unit="days" icon={FiRepeat} accent={PG.amber} />
        <KPICard title="DIFOT" value={branch.supply.difotSuppliers} unit="%" icon={FiTruck} accent={PG.cyan} />
        <KPICard title="SHRINKAGE" value={branch.supply.shrinkagePct} unit="%" icon={FiMinusCircle} accent={PG.amber} />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="BRANCH_STOCK_STATUS" subtitle={`${STOCK_ROWS.length} tracked SKUs`} className="col-span-7">
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2">On-hand</th>
                  <th className="text-right px-3 py-2">Reorder</th>
                  <th className="text-center px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {STOCK_ROWS.map((r) => {
                  const st = statusOf(r.onHand, r.reorder);
                  return (
                    <tr key={r.sku} className="border-t border-white/5">
                      <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{r.sku}</td>
                      <td className="px-3 py-2 text-neutral-300">{r.desc}</td>
                      <td className="px-3 py-2 text-right font-doto text-lg text-white">{r.onHand}</td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[10px] text-neutral-500">{r.reorder}</td>
                      <td className="px-3 py-2 text-center"><StatusPill tone={st.tone}>{st.label}</StatusPill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="OPEN_PURCHASE_ORDERS" subtitle={`${OPEN_POS.length} in-flight`} className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {OPEN_POS.map((p) => (
              <div key={p.po} className="px-4 py-3 border-b border-white/5">
                <div className="flex justify-between items-baseline">
                  <span className="font-mono-tech text-[10px] text-neutral-400">{p.po}</span>
                  <StatusPill tone={p.tone}>ETA {p.eta}</StatusPill>
                </div>
                <div className="text-white text-sm mt-1">{p.supplier}</div>
                <div className="flex gap-4 mt-1 font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                  <span>{p.lines} lines</span>
                  <span>{p.value}</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="CONSUMPTION_7D" subtitle="units issued / day" className="col-span-8">
          <div className="h-full p-2">
            <PGLineChart
              categories={['D-6', 'D-5', 'D-4', 'D-3', 'D-2', 'D-1', 'TDY']}
              series={[{ name: 'Units', data: consumption, itemStyle: { color: PG.cyan }, lineStyle: { color: PG.cyan, width: 2 } }]}
            />
          </div>
        </Panel>

        <Panel title="SHRINKAGE_90D" subtitle="rolling" className="col-span-4">
          <div className="h-full p-4 flex flex-col justify-center items-center">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Shrinkage</div>
            <div className="font-doto text-6xl text-white leading-none">{branch.supply.shrinkagePct}%</div>
            <div
              className="font-mono-tech text-[10px] uppercase tracking-widest mt-3"
              style={{ color: branch.supply.shrinkagePct < 1 ? PG.emerald : PG.amber }}
            >
              {branch.supply.shrinkagePct < 1 ? 'Within tolerance' : 'Above tolerance'}
            </div>
            <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 mt-6">Target &lt; 1.0%</div>
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
